/* assets/dealer-map.js
 * Store locator: keeps the list and the Google map in sync, plus search,
 * filtering and distance sorting. No dependencies.
 * The Google Maps JS API is only loaded once the map is about to scroll into view.
 */
(function () {
  'use strict';

  var MAPS_PROMISE = null;

  function loadMapsApi(key) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      return Promise.resolve();
    }
    if (MAPS_PROMISE) return MAPS_PROMISE;

    MAPS_PROMISE = new Promise(function (resolve, reject) {
      var cb = '__dealerMapsReady';
      window[cb] = function () { resolve(); };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
        '&v=weekly&libraries=marker&loading=async&callback=' + cb;
      script.async = true;
      script.onerror = function () { reject(new Error('Google Maps API failed to load')); };
      document.head.appendChild(script);
    });
    return MAPS_PROMISE;
  }

  function distanceKm(a, b) {
    var R = 6371;
    var dLat = ((b.lat - a.lat) * Math.PI) / 180;
    var dLng = ((b.lng - a.lng) * Math.PI) / 180;
    var la1 = (a.lat * Math.PI) / 180;
    var la2 = (b.lat * Math.PI) / 180;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(la1) * Math.cos(la2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function normalize(str) {
    return (str || '').toString().toLowerCase().trim();
  }

  function DealerLocator(root) {
    this.root = root;
    this.apiKey = root.dataset.apiKey || '';
    this.mapId = root.dataset.mapId || '';
    this.center = {
      lat: parseFloat(root.dataset.centerLat) || 64.5,
      lng: parseFloat(root.dataset.centerLng) || 26.0
    };
    this.zoom = parseInt(root.dataset.zoom, 10) || 5;

    // Translatable strings come from the section settings via data attributes.
    this.countOne = root.dataset.countOne || '1 location';
    this.countMany = root.dataset.countMany || '[count] locations';
    this.yourLocationLabel = root.dataset.yourLocation || 'Your location';

    this.listEl = root.querySelector('[data-dealer-list]');
    this.mapEl = root.querySelector('[data-dealer-map]');
    this.searchEl = root.querySelector('[data-dealer-search]');
    this.geoEl = root.querySelector('[data-dealer-geolocate]');
    this.countEl = root.querySelector('[data-dealer-count]');
    this.noResultsEl = root.querySelector('[data-dealer-no-results]');
    this.filterEls = Array.prototype.slice.call(root.querySelectorAll('[data-dealer-filter]'));

    this.activeFilter = '';
    this.query = '';
    this.selectedId = null;
    this.map = null;
    this.infoWindow = null;

    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-dealer]')).map(function (el) {
      var lat = parseFloat(el.dataset.lat);
      var lng = parseFloat(el.dataset.lng);
      return {
        el: el,
        id: el.dataset.id,
        name: el.dataset.name || '',
        lat: isFinite(lat) ? lat : null,
        lng: isFinite(lng) ? lng : null,
        types: (el.dataset.types || '').split('||').filter(Boolean),
        search: normalize(el.dataset.search),
        distanceEl: el.querySelector('[data-dealer-distance]'),
        popup: el.querySelector('[data-dealer-popup]'),
        marker: null,
        visible: true
      };
    });

    this.bindEvents();
    this.updateCount();

    if (this.apiKey && this.mapEl && this.items.some(function (i) { return i.lat !== null; })) {
      this.observeMap();
    } else if (this.mapEl) {
      // No API key or no coordinates: the list works on its own, hide the map.
      this.root.classList.add('dealer-locator--list-only');
    }
  }

  DealerLocator.prototype.bindEvents = function () {
    var self = this;

    if (this.searchEl) {
      var timer = null;
      this.searchEl.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          self.query = normalize(self.searchEl.value);
          self.applyFilters();
        }, 150);
      });
      this.searchEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });
    }

    this.filterEls.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.activeFilter = btn.dataset.dealerFilter || '';
        self.filterEls.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        self.applyFilters();
      });
    });

    this.items.forEach(function (item) {
      var button = item.el.querySelector('[data-dealer-select]');
      if (!button) return;
      button.addEventListener('click', function () {
        self.select(item.id, { pan: true });
      });
    });

    if (this.geoEl && 'geolocation' in navigator) {
      this.geoEl.hidden = false;
      this.geoEl.addEventListener('click', function () {
        self.geoEl.classList.add('is-loading');
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            self.geoEl.classList.remove('is-loading');
            self.sortByDistance({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          function () {
            self.geoEl.classList.remove('is-loading');
            self.geoEl.classList.add('is-error');
            setTimeout(function () { self.geoEl.classList.remove('is-error'); }, 3000);
          },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
        );
      });
    }
  };

  DealerLocator.prototype.observeMap = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.initMap(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { io.disconnect(); self.initMap(); }
      });
    }, { rootMargin: '400px' });
    io.observe(this.mapEl);
  };

  DealerLocator.prototype.initMap = function () {
    var self = this;
    loadMapsApi(this.apiKey)
      .then(function () {
        return Promise.all([
          google.maps.importLibrary('maps'),
          google.maps.importLibrary('marker')
        ]);
      })
      .then(function (libs) {
        var Map = libs[0].Map;
        var AdvancedMarkerElement = libs[1].AdvancedMarkerElement;

        self.map = new Map(self.mapEl, {
          center: self.center,
          zoom: self.zoom,
          mapId: self.mapId || 'DEMO_MAP_ID',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'cooperative'
        });

        self.infoWindow = new google.maps.InfoWindow({
          // Hides Google's own header row and close button, which otherwise
          // reserve odd empty space at the top of the popup.
          headerDisabled: true,
          minWidth: 260,
          maxWidth: 320,
          pixelOffset: new google.maps.Size(0, -6)
        });

        self.items.forEach(function (item) {
          if (item.lat === null || item.lng === null) return;
          var pin = document.createElement('div');
          pin.className = 'dealer-pin';
          pin.setAttribute('role', 'img');
          pin.setAttribute('aria-label', item.name);
          pin.innerHTML =
            '<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">' +
            '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20c0-6.6-5.4-12-12-12z" fill="currentColor"/>' +
            '<circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>';

          // Note: no title option — it would render the browser's own black
          // tooltip on top of the pin.
          item.marker = new AdvancedMarkerElement({
            map: self.map,
            position: { lat: item.lat, lng: item.lng },
            content: pin,
            gmpClickable: true
          });

          item.marker.addListener('click', function () {
            self.select(item.id, { pan: true, scrollList: true });
          });
        });

        self.root.classList.add('dealer-locator--map-ready');
        self.fitToVisible();
      })
      .catch(function () {
        self.root.classList.add('dealer-locator--list-only');
      });
  };

  DealerLocator.prototype.matches = function (item) {
    if (this.activeFilter && item.types.indexOf(this.activeFilter) === -1) return false;
    if (this.query && item.search.indexOf(this.query) === -1) return false;
    return true;
  };

  DealerLocator.prototype.applyFilters = function () {
    var self = this;
    var visibleCount = 0;

    this.items.forEach(function (item) {
      var show = self.matches(item);
      item.visible = show;
      item.el.hidden = !show;
      if (item.marker) item.marker.map = show ? self.map : null;
      if (show) visibleCount++;
    });

    if (this.noResultsEl) this.noResultsEl.hidden = visibleCount !== 0;
    this.updateCount(visibleCount);
    this.fitToVisible();
  };

  DealerLocator.prototype.updateCount = function (count) {
    if (!this.countEl) return;
    var n = typeof count === 'number' ? count : this.items.length;
    this.countEl.textContent =
      n === 1 ? this.countOne : this.countMany.replace('[count]', n);
  };

  DealerLocator.prototype.fitToVisible = function () {
    if (!this.map) return;
    var pts = this.items.filter(function (i) { return i.visible && i.lat !== null; });
    if (!pts.length) return;

    if (pts.length === 1) {
      this.map.setCenter({ lat: pts[0].lat, lng: pts[0].lng });
      this.map.setZoom(12);
      return;
    }
    var bounds = new google.maps.LatLngBounds();
    pts.forEach(function (p) { bounds.extend({ lat: p.lat, lng: p.lng }); });
    this.map.fitBounds(bounds, 48);
  };

  DealerLocator.prototype.select = function (id, opts) {
    opts = opts || {};
    var self = this;
    var item = this.items.filter(function (i) { return i.id === id; })[0];
    if (!item) return;

    this.selectedId = id;
    this.items.forEach(function (i) {
      var active = i === item;
      i.el.classList.toggle('is-selected', active);
      if (i.marker && i.marker.content) {
        i.marker.content.classList.toggle('is-active', active);
        i.marker.zIndex = active ? 10 : 1;
      }
    });

    if (this.map && item.lat !== null) {
      if (opts.pan) {
        this.map.panTo({ lat: item.lat, lng: item.lng });
        if (this.map.getZoom() < 10) this.map.setZoom(11);
      }
      if (this.infoWindow && item.popup) {
        var wrap = document.createElement('div');
        wrap.className = 'dealer-popup-root';
        wrap.appendChild(item.popup.content.cloneNode(true));

        var closeBtn = wrap.querySelector('[data-popup-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () { self.infoWindow.close(); });
        }

        this.infoWindow.setContent(wrap);
        this.infoWindow.open({ anchor: item.marker, map: this.map });
      }
    }

    if (opts.scrollList && item.el.scrollIntoView) {
      item.el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      var btn = item.el.querySelector('[data-dealer-select]');
      if (btn) btn.focus({ preventScroll: true });
    }

    if (self.root.dataset.trackEvents === 'true' && typeof window.gtag === 'function') {
      window.gtag('event', 'dealer_select', { dealer: item.name });
    }
  };

  DealerLocator.prototype.sortByDistance = function (origin) {
    var self = this;
    var withCoords = this.items.filter(function (i) { return i.lat !== null; });

    withCoords.forEach(function (item) {
      item.distance = distanceKm(origin, { lat: item.lat, lng: item.lng });
      if (item.distanceEl) {
        item.distanceEl.textContent =
          item.distance < 10
            ? item.distance.toFixed(1) + ' km'
            : Math.round(item.distance) + ' km';
      }
    });

    withCoords
      .slice()
      .sort(function (a, b) { return a.distance - b.distance; })
      .forEach(function (item) { self.listEl.appendChild(item.el); });

    this.root.classList.add('dealer-locator--has-distances');

    if (this.map) {
      this.map.setCenter(origin);
      this.map.setZoom(9);
      if (!this.originMarker) {
        var dot = document.createElement('div');
        dot.className = 'dealer-origin';
        dot.setAttribute('role', 'img');
        dot.setAttribute('aria-label', this.yourLocationLabel);
        google.maps.importLibrary('marker').then(function (lib) {
          self.originMarker = new lib.AdvancedMarkerElement({
            map: self.map,
            position: origin,
            content: dot
          });
        });
      } else {
        this.originMarker.position = origin;
      }
    }

    var nearest = withCoords.slice().sort(function (a, b) { return a.distance - b.distance; })[0];
    if (nearest) this.select(nearest.id, { pan: false });
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-dealer-locator]').forEach(function (el) {
      if (el.dataset.dealerInit === 'true') return;
      el.dataset.dealerInit = 'true';
      new DealerLocator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: the section is re-rendered while editing
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();